import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PageHeader from '../../../components/PageHeader';
import ImageUploader from '../../../components/common/Form/ImageUploader';
import Button from '../../../components/common/Button';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import { uploadRestaurantImages } from '../../../api/restaurant';
import { ImageInterface } from '../../../types/ImageInterface';

type LocationImage = Blob | string | null;

const changeOf = (saved: ImageInterface | undefined, image: LocationImage) => ({
  upload: image instanceof Blob ? image : null,
  deleteID: saved?.imageID != null && (image instanceof Blob || !image) ? saved.imageID : undefined
});

const ComingSoonPage = () => {
  const { currentRestaurant, updateRestaurant } = useRestaurantContext();
  const [thumbnailImage, setThumbnailImage] = useState<LocationImage>(null);
  const [menuCoverImage, setMenuCoverImage] = useState<LocationImage>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setThumbnailImage(currentRestaurant?.images?.thumbnail?.imageURL ?? null);
    setMenuCoverImage(currentRestaurant?.images?.menuCover?.imageURL ?? null);
  }, [currentRestaurant?.restaurantID, currentRestaurant?.images]);

  const handleSave = async () => {
    if (!currentRestaurant || isSaving) return;

    const thumbnail = changeOf(currentRestaurant.images?.thumbnail, thumbnailImage);
    const menuCover = changeOf(currentRestaurant.images?.menuCover, menuCoverImage);
    const imagesToDelete = [thumbnail.deleteID, menuCover.deleteID].filter((id): id is number => id !== undefined);
    if (!thumbnail.upload && !menuCover.upload && !imagesToDelete.length) return;

    setIsSaving(true);
    try {
      const uploaded = await uploadRestaurantImages(
        null,
        thumbnail.upload,
        null,
        menuCover.upload,
        null,
        [],
        [],
        imagesToDelete,
        [],
        []
      );
      updateRestaurant({
        ...currentRestaurant,
        images: {
          ...currentRestaurant.images,
          thumbnail: uploaded?.thumbnail ?? (thumbnail.deleteID ? undefined : currentRestaurant.images?.thumbnail),
          menuCover: uploaded?.menuCover ?? (menuCover.deleteID ? undefined : currentRestaurant.images?.menuCover)
        }
      });
      toast.success('Photos saved successfully.');
    } catch (error) {
      console.error('Failed to save location photos:', error);
      toast.error('Failed to save photos.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="coming-soon-page">
      <PageHeader title="COMING SOON" />
      <div className="coming-soon-page__content">
        <section className="coming-soon-page__section">
          <h2 className="coming-soon-page__heading">SEARCH PHOTO</h2>
          <p className="coming-soon-page__description">
            Choose your favorite photo of this location for your thumbnail photo. This photo will be displayed in
            TapTab&apos;s restaurant list. It should be at least 100 px wide &amp; 100 px tall and will be cropped to fit
            a 4:6 aspect ratio.
          </p>
          <ImageUploader id="location-thumbnail" image={thumbnailImage} onUpdate={setThumbnailImage} />
        </section>

        <section className="coming-soon-page__section">
          <h2 className="coming-soon-page__heading">MENU PHOTO</h2>
          <p className="coming-soon-page__description">
            Choose a photo that will appear at the top of this location&apos;s menu. It should be at least 100 px wide
            &amp; 100 px tall.
          </p>
          <ImageUploader id="location-menu-cover" image={menuCoverImage} onUpdate={setMenuCoverImage} />
        </section>

        <Button className="coming-soon-page__save-button" onClick={handleSave} isDisabled={!currentRestaurant || isSaving}>
          {isSaving ? 'SAVING...' : 'SAVE'}
        </Button>
      </div>
    </div>
  );
};

export default ComingSoonPage;
